import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PartyPopper, CreditCard, Lock } from 'lucide-react';
import { findPlan, formatAnnual, formatMonthly, monthsFree, savingPct, usePlanCatalogue } from '../planPrices';
import { createPayPalOrder, capturePayPalOrder, createSubscription, initiateCheckout,
  fetchPaymentMethods, offersPayPal, PaymentMethods } from '../../../api/billingApi';
import { useBilling } from '../../../context/BillingContext';
import { Eyebrow, PageHeading } from '../../../design-system';
import styles from './CheckoutPage.module.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? '');



// ── CC Form (Stripe Elements) ─────────────────────────────────────────────────
const CCForm: React.FC<{
  planTier: string;
  billingCycle: 'monthly' | 'annual';
  onSuccess: () => void;
}> = ({ planTier, billingCycle, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { refresh } = useBilling();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);
    try {
      const card = elements.getElement(CardElement);
      if (!card) throw new Error('Card element not found');
      const { paymentMethod, error: stripeError } = await stripe.createPaymentMethod({
        type: 'card',
        card,
      });
      if (stripeError) throw new Error(stripeError.message);
      await createSubscription({
        plan_tier: planTier,
        billing_cycle: billingCycle,
        payment_gateway: 'stripe',
        stripe_payment_method_id: paymentMethod!.id,
      });
      await refresh();
      onSuccess();
    } catch (err: any) {
      setError(err.message ?? 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.ccForm}>
      <label className={styles.cardLabel}>Card details</label>
      <div className={styles.cardElement}>
        <CardElement
          options={{
            style: {
              base: { fontSize: '15px', color: 'var(--ds-surface-2)', '::placeholder': { color: 'var(--ds-text-faint)' }, iconColor: 'var(--ds-brand-blue-light)' },
              invalid: { color: 'var(--ds-danger-border)' },
            },
          }}
        />
      </div>
      {error && <div className={styles.errorMsg}>{error}</div>}
      <button type="submit" disabled={!stripe || loading} className={styles.payBtn}>
        {loading ? 'Processing…' : 'Pay with Card'}
      </button>
    </form>
  );
};

// ── Main Checkout Page ────────────────────────────────────────────────────────
const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useBilling();

  const planTier = searchParams.get('plan') ?? 'professional';
  const defaultCycle = (searchParams.get('cycle') as 'monthly' | 'annual') ?? 'annual';

  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(defaultCycle);
  const [gateway, setGateway] = useState<'paypal' | 'stripe'>('stripe');
  const [success, setSuccess] = useState(false);
  // Which gateways the backend will accept. PayPal is withdrawn until its live
  // credentials exist, and asking is the only honest way to know: a button
  // that reaches PayPal and is refused reads as a broken product.
  const [methods, setMethods] = useState<PaymentMethods | null>(null);
  useEffect(() => { fetchPaymentMethods().then(setMethods).catch(() => setMethods({ gateways: ['stripe'] })); }, []);

  // Record a pending payment history entry as soon as user opens checkout,
  // so it appears in history even if they navigate away without completing.
  useEffect(() => {
    initiateCheckout(planTier, defaultCycle, 'stripe').catch(() => {
      // Non-critical — silently ignore if the user isn't authenticated yet
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catalogue = usePlanCatalogue();
  const found = findPlan(catalogue, planTier);
  // The amount shown is the amount charged: both come from the same catalogue row.
  const planName = found?.display_name ?? planTier;
  const saving = savingPct(found);
  const savingLabel = saving !== null && saving > 0 ? `Save ${saving}%` : null;
  const freeMonths = monthsFree(found);
  const displayPrice = !catalogue ? '…'
    : !found ? 'Custom'
    : (billingCycle === 'annual' ? formatAnnual(found) : formatMonthly(found)) ?? 'Custom';

  const handleSuccess = async () => {
    await refresh();
    setSuccess(true);
    setTimeout(() => navigate('/billing'), 2500);
  };

  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID ?? 'test';
  const paypalOffered = offersPayPal(methods);

  if (success) {
    return (
      <div className={styles.successPage}>
        <div className={styles.successIcon}><PartyPopper size={20} aria-hidden /></div>
        <h2>Subscription activated!</h2>
        <p>Redirecting to your billing dashboard…</p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <button className={styles.back} onClick={() => navigate(-1)}>← Back</button>

      <div className={styles.container}>
        {/* Order summary */}
        <div className={styles.summary}>
          <Eyebrow tone="default" dot>CHECKOUT</Eyebrow>
          <PageHeading
            accentWord="Summary"
            accentTone="orange"
            size="h3"
            align="left"
            level="h2"
            className={styles.summaryTitle}
          >
            Order Summary
          </PageHeading>
          <div className={styles.summaryPlan}>{planName}</div>

          <div className={styles.cycleToggle}>
            <button
              className={`${styles.cycleBtn} ${billingCycle === 'monthly' ? styles.cycleActive : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >Monthly</button>
            <button
              className={`${styles.cycleBtn} ${billingCycle === 'annual' ? styles.cycleActive : ''}`}
              onClick={() => setBillingCycle('annual')}
            >
              Annual {savingLabel && <span className={styles.savePill}>{savingLabel}</span>}
            </button>
          </div>

          <div className={styles.priceBox}>
            <span className={styles.priceLabel}>Total today</span>
            <span className={styles.priceVal}>{displayPrice}</span>
          </div>

          <ul className={styles.summaryFeatures}>
            <li>✓ Full access during trial period</li>
            <li>✓ Cancel anytime</li>
            <li>✓ Secure payment</li>
            {billingCycle === 'annual' && freeMonths !== null && freeMonths > 0 && <li>✓ {freeMonths} month{freeMonths === 1 ? '' : 's'} free vs monthly</li>}
          </ul>
        </div>

        {/* Payment */}
        <div className={styles.payment}>
          <h2 className={styles.paymentTitle}>Payment Method</h2>

          {paypalOffered && (
            <div className={styles.gatewayTabs}>
              <button
                className={`${styles.gatewayTab} ${gateway === 'stripe' ? styles.gatewayActive : ''}`}
                onClick={() => setGateway('stripe')}
              >
                <CreditCard size={16} aria-hidden /> Credit / Debit Card
              </button>
              <button
                className={`${styles.gatewayTab} ${gateway === 'paypal' ? styles.gatewayActive : ''}`}
                onClick={() => setGateway('paypal')}
              >
                <img src="https://www.paypalobjects.com/webstatic/mktg/Logo/pp-logo-100px.png"
                  alt="PayPal" height={18} style={{ verticalAlign: 'middle' }} />
                {' '}PayPal
              </button>
            </div>
          )}

          {(gateway === 'stripe' || !paypalOffered) && (
            <Elements stripe={stripePromise} options={{ appearance: { theme: 'night' } }}>
              <CCForm planTier={planTier} billingCycle={billingCycle} onSuccess={handleSuccess} />
            </Elements>
          )}

          {gateway === 'paypal' && (
            <div className={styles.paypalWrapper}>
              <PayPalScriptProvider options={{ clientId: paypalClientId, currency: 'USD' }}>
                <PayPalButtons
                  style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay' }}
                  createOrder={async () => {
                    const result = await createPayPalOrder(planTier, billingCycle);
                    return result.order_id;
                  }}
                  onApprove={async (data) => {
                    await capturePayPalOrder(data.orderID, planTier, billingCycle);
                    await handleSuccess();
                  }}
                  onError={(err) => {
                    console.error('PayPal error', err);
                  }}
                />
              </PayPalScriptProvider>
            </div>
          )}

          <p className={styles.secureNote}>
            <Lock size={16} aria-hidden /> Payments are encrypted and secure. We never store your card details.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
